import React, { FC, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageSection from '../../HOC/PageSection/PageSection';
import { useFilters } from '../../hooks/useFilters';
import { IPredicStates } from '../../services/reports';
import ReportResultsLegend from '../ReportResultsLegend/ReportResultsLegend';
import SectionTabs from '../SectionTabs/SectionTabs';
import ReportPredictiveStatesHeader from './ReportPredictiveStatesHeader/ReportPredictiveStatesHeader';
import { ReportPredictiveStatesTableCont, Header } from './ReportPredictiveStatesTable.styles';
import ReportPredictiveStatesTile from './ReportPredictiveStatesTile/ReportPredictiveStatesTile';

interface IReportPredictiveStatesTable {
  description?: string;
  negativeStates?: IPredicStates;
  positiveStates?: IPredicStates;
  sectionTitle: ReactElement;
  isExecutiveReport: boolean;
}

const ReportPredictiveStatesTable: FC<IReportPredictiveStatesTable> = ({
  description,
  negativeStates,
  positiveStates,
  sectionTitle,
  isExecutiveReport,
}) => {
  const { currentFilters, applyAndMergeFilters } = useFilters();
  const { t } = useTranslation();

  const sectionTabs = [
    { name: t('report.general.totals'), value: 'totals' },
    { name: t('report.general.teams'), value: 'teams' },
  ];

  const getPSfilters = useMemo(() => {
    const predictiveStatesFilter = currentFilters?.predictiveStates;
    if (predictiveStatesFilter) {
      // @ts-ignore
      const psFilters = JSON.parse(predictiveStatesFilter);

      return {
        mode: psFilters?.mode || 'totals',
        legend: psFilters?.legend || false,
      };
    }
  }, [currentFilters?.predictiveStates]);
  const isTotalsTabActive = useMemo(() => getPSfilters?.mode !== 'teams', [getPSfilters]);

  return (
    <>
      <Header>
        {sectionTitle}
        {isExecutiveReport && (
          <SectionTabs
            activeTab={getPSfilters?.mode || 'totals'}
            setTab={(tab: string) => applyAndMergeFilters('predictiveStates', 'mode', tab)}
            tabs={sectionTabs}
          />
        )}
      </Header>
      <PageSection>
        <ReportPredictiveStatesTableCont id="ReportPredictiveStatesTableCont">
          <ReportResultsLegend
            noBorder
            description={description}
            expandLegend={(status: boolean) =>
              applyAndMergeFilters('predictiveStates', 'legend', status)
            }
            legendExpanded={!!getPSfilters?.legend}
          />
          <ReportPredictiveStatesHeader sortKey="filters" isTotalsTabActive={isTotalsTabActive} />
          <ReportPredictiveStatesTile
            sortKey="filters"
            noDataInfo={t('report.predictiveStates.noDataInfo')}
            topicResults={[
              ...(positiveStates ? [...positiveStates.topicResults] : []),
              ...(negativeStates ? [...negativeStates.topicResults] : []),
            ]}
            {...{ isTotalsTabActive }}
          />
        </ReportPredictiveStatesTableCont>
      </PageSection>
    </>
  );
};

export default ReportPredictiveStatesTable;
